import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Box,
    Title,
    Text,
    TextInput,
    Button,
    Table,
    Paper,
    Checkbox,
    Alert,
    Collapse,
    ActionIcon,
    Modal,
    Stack,
    Group,
    Loader,
} from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconUserSearch } from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import ImpersonationService from '../../services/ImpersonationService';
import { scimPersonService } from '../../services/ScimPersonService';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import type { Person } from '../../types/Person';
import { MODAL_SIZES } from '../../theme/mantineTheme';

const PAGE_SIZE = 100;
const FAUX_ENTITY_TYPES = ['FunctionCatalog'];

function distinctPolicyRules(resources: any[]): any[] {
    const byId = new Map<string, any>();
    for (const resource of resources || []) {
        const rules = resource?.$PolicyRules ?? resource?.$policyRules;
        if (!Array.isArray(rules)) continue;
        for (const rule of rules) {
            const id = rule?.Id ?? rule?.id ?? JSON.stringify(rule);
            if (!byId.has(id)) byId.set(id, rule);
        }
    }
    return Array.from(byId.values());
}

export interface AuditRowResult {
    entityType: string;
    totalResults: number;
    policyRules: any[];
    policyRulesCount: number;
    durationMs?: number;
    error?: string;
}

type RowState = { status: 'loading' } | AuditRowResult;

function PersonUpnSelectorDialog({
    open,
    onClose,
    onSelect
}: {
    open: boolean;
    onClose: () => void;
    onSelect: (upn: string) => void;
}) {
    const [searchText, setSearchText] = useState('');
    const [omitSearchDialectExpression, setOmitSearchDialectExpression] = useState(false);
    const [persons, setPersons] = useState<Person[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);

    const doSearch = useCallback(async () => {
        setLoading(true);
        setError(null);
        setPersons([]);
        setSelectedPerson(null);
        try {
            const response = await scimPersonService.searchPersons({
                searchtext: searchText.trim() || undefined,
                page: 1,
                pageSize: 50,
                sortBy: 'DisplayName:ascending',
                omitSearchDialectExpression
            });
            setPersons(response.Resources ?? []);
        } catch (err: any) {
            setError(err?.message ?? 'Search failed');
        } finally {
            setLoading(false);
        }
    }, [searchText, omitSearchDialectExpression]);

    const handleOk = useCallback(() => {
        if (selectedPerson?.UserPrincipalName) {
            onSelect(selectedPerson.UserPrincipalName);
            onClose();
        }
    }, [selectedPerson, onSelect, onClose]);

    const handleClose = useCallback(() => {
        setSearchText('');
        setPersons([]);
        setSelectedPerson(null);
        setError(null);
        onClose();
    }, [onClose]);

    return (
        <Modal opened={open} onClose={handleClose} title="Select person (UPN)" size={MODAL_SIZES.confirm}>
            <Stack gap="md" mb="md" mt="xs">
                <Group gap="xs">
                    <TextInput
                        flex={1}
                        size="sm"
                        placeholder="Search by name or UPN…"
                        value={searchText}
                        onChange={(e) => setSearchText(e.currentTarget.value)}
                        onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                        leftSection={<IconUserSearch size={18} />}
                    />
                    <Button onClick={doSearch} loading={loading}>
                        Search
                    </Button>
                </Group>
                <Checkbox
                    label="Expression (omit searchdialect=expression)"
                    checked={omitSearchDialectExpression}
                    onChange={(e) => setOmitSearchDialectExpression(e.currentTarget.checked)}
                    size="sm"
                />
            </Stack>
            {error && (
                <Alert color="red" mb="md" withCloseButton onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            <Paper withBorder style={{ maxHeight: 320, overflow: 'auto' }}>
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>DisplayName</Table.Th>
                            <Table.Th>UserPrincipalName</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {persons.map((p) => (
                            <Table.Tr
                                key={p.Id}
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: selectedPerson?.Id === p.Id ? 'var(--mantine-color-blue-light)' : undefined,
                                }}
                                onClick={() => setSelectedPerson(p)}
                            >
                                <Table.Td>{p.DisplayName ?? '—'}</Table.Td>
                                <Table.Td>{p.UserPrincipalName ?? p.Username ?? '—'}</Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Paper>
            {persons.length === 0 && !loading && searchText.trim() && (
                <Text size="sm" c="dimmed" mt="md">
                    No results. Try a different search.
                </Text>
            )}
            <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={handleClose}>Cancel</Button>
                <Button onClick={handleOk} disabled={!selectedPerson?.UserPrincipalName}>
                    OK
                </Button>
            </Group>
        </Modal>
    );
}

const PolicyRuleAudit: React.FC = () => {
    const { allEntityTypes, isLoading: entityTypesLoading, error: entityTypesError } = useEntityTypes();
    const visibleEntityTypes = useMemo(() => {
        const fromContext = [...(allEntityTypes ?? [])];
        const lowerSet = new Set(fromContext.map((t) => t.toLowerCase()));
        for (const t of FAUX_ENTITY_TYPES) {
            if (!lowerSet.has(t.toLowerCase())) {
                fromContext.push(t);
                lowerSet.add(t.toLowerCase());
            }
        }
        return fromContext.sort((a, b) => a.localeCompare(b));
    }, [allEntityTypes]);

    const [upn, setUpn] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<Record<string, boolean>>({});

    useEffect(() => {
        const stored = ImpersonationService.getPersistedOnBehalfOfUpn();
        if (stored) setUpn(stored);
    }, []);
    const [filterByType, setFilterByType] = useState<Record<string, string>>({});
    const [upnSelectorOpen, setUpnSelectorOpen] = useState(false);

    const setFilterForType = useCallback((entityType: string, value: string) => {
        setFilterByType((prev) => ({ ...prev, [entityType]: value }));
    }, []);

    useEffect(() => {
        if (visibleEntityTypes.length === 0) return;
        setSelectedTypes((prev) => {
            const next = { ...prev };
            let changed = false;
            visibleEntityTypes.forEach((t) => {
                if (next[t] === undefined) { next[t] = true; changed = true; }
            });
            return changed ? next : prev;
        });
    }, [visibleEntityTypes]);
    const [resultsByType, setResultsByType] = useState<Record<string, RowState>>({});
    const [countOnly, setCountOnly] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);

    const setAllSelected = useCallback((checked: boolean) => {
        setSelectedTypes((prev) => {
            const next = { ...prev };
            visibleEntityTypes.forEach((t) => { next[t] = checked; });
            return next;
        });
    }, [visibleEntityTypes]);

    const toggleType = useCallback((entityType: string) => {
        setSelectedTypes((prev) => ({ ...prev, [entityType]: !prev[entityType] }));
    }, []);

    const runAudit = useCallback(async () => {
        const trimmedUpn = upn?.trim();
        if (!trimmedUpn) {
            setError('Enter a UPN to run the audit.');
            return;
        }
        setError(null);
        setLoading(true);
        const selected = visibleEntityTypes.filter((t) => selectedTypes[t]);

        setResultsByType((prev) => {
            const next = { ...prev };
            selected.forEach((t) => { next[t] = { status: 'loading' }; });
            return next;
        });

        const updateResult = (entityType: string, result: AuditRowResult) => {
            setResultsByType((prev) => ({ ...prev, [entityType]: result }));
        };

        const AUDIT_ATTRIBUTES = ['Id'] as const;

        const fetchOne = async (entityType: string): Promise<void> => {
            const start = performance.now();
            try {
                const normalized = entityType.toLowerCase();
                if (normalized === 'functioncatalog') {
                    const raw = await entityService.getFunctionCatalog(undefined, trimmedUpn);
                    const durationMs = Math.round(performance.now() - start);
                    type CatalogResponse = any[] | { Resources?: unknown[]; resources?: unknown[]; totalResults?: number };
                    const data = raw as CatalogResponse;
                    const resources = Array.isArray(data) ? data : (data?.Resources ?? data?.resources ?? []);
                    const totalResults = Array.isArray(data) ? data.length : (data?.totalResults ?? resources.length ?? 0);
                    const rules = distinctPolicyRules(resources);
                    updateResult(entityType, { entityType, totalResults, policyRules: rules, policyRulesCount: rules.length, durationMs });
                } else {
                    const extraFilter = filterByType[entityType]?.trim() || undefined;
                    const response = await entityService.getEntitiesByType(
                        normalized,
                        1,
                        PAGE_SIZE,
                        undefined,
                        extraFilter,
                        [...AUDIT_ATTRIBUTES],
                        undefined,
                        undefined,
                        undefined,
                        trimmedUpn,
                        countOnly
                    );
                    const durationMs = Math.round(performance.now() - start);
                    const resources = response?.data ?? response?.items ?? [];
                    const totalResults = response?.totalResults ?? 0;
                    const rules = distinctPolicyRules(resources);
                    updateResult(entityType, { entityType, totalResults, policyRules: rules, policyRulesCount: rules.length, durationMs });
                }
            } catch (err: any) {
                const durationMs = Math.round(performance.now() - start);
                updateResult(entityType, {
                    entityType,
                    totalResults: 0,
                    policyRules: [],
                    policyRulesCount: 0,
                    durationMs,
                    error: err?.message ?? err?.response?.data?.message ?? 'Request failed'
                });
            }
        };

        await Promise.all(selected.map((t) => fetchOne(t)));
        setLoading(false);
    }, [upn, selectedTypes, visibleEntityTypes, filterByType, countOnly, entityService]);

    const allSelected = visibleEntityTypes.length > 0 && visibleEntityTypes.every((t) => selectedTypes[t]);
    const someSelected = visibleEntityTypes.some((t) => selectedTypes[t]);

    return (
        <Box p="md">
            <Title order={5} mb="xs">
                Policy Rule Audit
            </Title>
            <Text size="sm" c="dimmed" mb="md">
                Enter a UPN and run the audit. Each selected entity type is queried with the <code>x-on-behalf-of</code> header set to that UPN.
                The entity types listed are the same as in the navigation (from your current or on-behalf-of entitlements).
            </Text>

            <Group align="flex-start" gap="xs" mb="md">
                <TextInput
                    label="UPN (on-behalf-of)"
                    value={upn}
                    onChange={(e) => setUpn(e.currentTarget.value)}
                    placeholder="user@domain.com"
                    miw={320}
                    maw={400}
                    aria-label="UPN for policy rule audit"
                />
                <Button
                    variant="outline"
                    onClick={() => setUpnSelectorOpen(true)}
                    leftSection={<IconUserSearch size={18} />}
                    mt="xl"
                >
                    Select
                </Button>
            </Group>
            <PersonUpnSelectorDialog
                open={upnSelectorOpen}
                onClose={() => setUpnSelectorOpen(false)}
                onSelect={(selectedUpn) => {
                    setUpn(selectedUpn);
                    setUpnSelectorOpen(false);
                }}
            />

            {entityTypesLoading && (
                <Text c="dimmed" mb="xs">Loading entity types…</Text>
            )}
            {entityTypesError && (
                <Alert color="blue" mb="xs">Entity types could not be loaded. {entityTypesError}</Alert>
            )}

            <Box mb="md">
                <Button onClick={runAudit} loading={loading} disabled={!someSelected}>
                    Audit
                </Button>
            </Box>

            {error && (
                <Alert color="yellow" mb="md" withCloseButton onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Paper withBorder mb="md">
                <Group px="md" py="xs" align="center" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                    <Checkbox
                        label="CountOnly"
                        checked={countOnly}
                        onChange={(e) => setCountOnly(e.currentTarget.checked)}
                        disabled={loading}
                        size="sm"
                    />
                    <Text size="xs" c="dimmed" ml="xs">
                        When checked, pass countOnly=true in all audit calls (no resource data returned).
                    </Text>
                </Group>
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th w={48}>
                                <Checkbox
                                    checked={allSelected}
                                    indeterminate={someSelected && !allSelected}
                                    onChange={() => setAllSelected(!allSelected)}
                                />
                            </Table.Th>
                            <Table.Th>Entity Type</Table.Th>
                            <Table.Th miw={180}>Extra filter</Table.Th>
                            <Table.Th ta="right">totalResults</Table.Th>
                            <Table.Th ta="right">Policy Rules (distinct)</Table.Th>
                            <Table.Th ta="right">Duration</Table.Th>
                            <Table.Th>Error</Table.Th>
                            <Table.Th w={48} />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {visibleEntityTypes.map((entityType) => {
                            const rowState = resultsByType[entityType];
                            const isLoading = rowState != null && 'status' in rowState && rowState.status === 'loading';
                            const result = rowState != null && !isLoading ? (rowState as AuditRowResult) : null;
                            return (
                                <React.Fragment key={entityType}>
                                    <Table.Tr>
                                        <Table.Td>
                                            <Checkbox
                                                checked={!!selectedTypes[entityType]}
                                                onChange={() => toggleType(entityType)}
                                            />
                                        </Table.Td>
                                        <Table.Td>{entityType}</Table.Td>
                                        <Table.Td miw={180}>
                                            <TextInput
                                                size="xs"
                                                placeholder={'e.g. DisplayName co "x"'}
                                                value={filterByType[entityType] ?? ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterForType(entityType, e.currentTarget.value)}
                                                disabled={loading}
                                                aria-label={`Extra filter for ${entityType}`}
                                                styles={{ input: { fontSize: '0.875rem' } }}
                                            />
                                        </Table.Td>
                                        <Table.Td ta="right">
                                            {isLoading ? (
                                                <Loader size={20} />
                                            ) : result != null ? (
                                                result.totalResults
                                            ) : (
                                                '—'
                                            )}
                                        </Table.Td>
                                        <Table.Td ta="right">
                                            {isLoading ? '—' : result != null ? result.policyRulesCount : '—'}
                                        </Table.Td>
                                        <Table.Td ta="right">
                                            {isLoading ? '—' : result != null && result.durationMs != null ? (
                                                result.durationMs >= 1000
                                                    ? `${(result.durationMs / 1000).toFixed(2)} s`
                                                    : `${result.durationMs} ms`
                                            ) : '—'}
                                        </Table.Td>
                                        <Table.Td>
                                            {isLoading ? '—' : (result?.error ?? '—')}
                                        </Table.Td>
                                        <Table.Td>
                                            {result != null && result.policyRulesCount > 0 && (
                                                <ActionIcon
                                                    size="sm"
                                                    variant="subtle"
                                                    onClick={() => setExpandedRow(expandedRow === entityType ? null : entityType)}
                                                >
                                                    {expandedRow === entityType ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                                                </ActionIcon>
                                            )}
                                        </Table.Td>
                                    </Table.Tr>
                                    <Table.Tr>
                                        <Table.Td colSpan={8} style={{ padding: 0, borderBottom: 0, verticalAlign: 'top' }}>
                                            <Collapse in={expandedRow === entityType}>
                                                {result != null && (
                                                    <Box py="xs" pl="md" bg="var(--mantine-color-default-hover)">
                                                        <Text size="xs" c="dimmed">
                                                            Distinct $PolicyRules ({result.policyRules.length})
                                                        </Text>
                                                        <Box component="pre" fz="xs" mt="xs" mah={300} style={{ overflow: 'auto' }}>
                                                            {JSON.stringify(result.policyRules, null, 2)}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Collapse>
                                        </Table.Td>
                                    </Table.Tr>
                                </React.Fragment>
                            );
                        })}
                    </Table.Tbody>
                </Table>
            </Paper>

            <Button onClick={runAudit} loading={loading} disabled={!someSelected}>
                Audit
            </Button>
        </Box>
    );
};

export default PolicyRuleAudit;
